<template>
  <div class="warranty">
    <Loader
      v-if="loaderEnabled"
      :logo="loaderLogo"
    ></Loader>
    <KTCodePreview v-bind:title="title">
      <template v-slot:toolbar>
        <div class="row">
          <div class="ml-3">
            <router-link
              :to="addNewRoute"
              v-if="showAddButton"
            >
              <button
                type="button"
                class="btn btn-primary font-weight-bolder btn-sm"
              >
                <i
                  style="font-size: 1rem"
                  class="flaticon2-add-1"
                ></i
                >Thêm mới
              </button>
            </router-link>
          </div>
        </div>
      </template>
      <template v-slot:preview>
        <warranty-search-bar
          :params="apiParams"
          :page-size="selectedPageSize"
          :status-options="statusOptions"
          :option-stores="optionStores"
          :all-option-stores="allOptionStores"
          :option-page-size="optionPageSize"
          :is-loading="isLoading"
          :warranty-service-options="warrantyServiceOptions"
          :list-company="listCompany"
          @filter="onFilter"
          @reset="onReset"
          @page-size-change="onPageSizeChange"
        />
        <warranty-list-table
          detail-mode="reception"
          :items="listItem"
          :fields="fields"
          :is-loading="isLoading"
          :total-items="totalItems"
          :number-of-page="numberOfPage"
          :current-page="currentPage"
          :limit="limit"
          :can-create-lending-stock-slip="false"
          :can-edit="checkPermission('WARRANTY_RECEPTION_UPDATE')"
          :can-delete="checkPermission('WARRANTY_RECEPTION_DELETE')"
          :can-print="false"
          :can-change-store="false"
          :can-update-unlimit="checkPermission('WARRANTY_UPDATE_UNLIMIT')"
          @edit="editItem"
          @view-detail="viewWarrantyDetail"
          @delete="onDeleteItem"
          @page-change="onPageChange"
        />
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue'
import { SET_BREADCRUMB } from '@/core/services/store//modules/breadcrumbs.module'
import { showDeleteAlert } from '@/utils/sweet-alert2'
import { mapGetters } from 'vuex'
import Loader from '@/view/content/Loader.vue'
import permissionMixin from '@/view/mixins/permission.mixin'
import { formatDBDate } from '@/utils/date'
import { getWarranties, deleteWarrantyReception, getWarrantyServices } from './utils/warrantyApi'
import { mapWarrantyListItem } from './utils/listHelpers'
import WarrantySearchBar from './components/WarrantySearchBar.vue'
import WarrantyListTable from './components/WarrantyListTable.vue'
import { WARRANTY_STATUS_OPTIONS } from '@/utils/constants'
import apiCommonMixin from '@/view/mixins/api-common.mixins'

export default {
  mixins: [permissionMixin, apiCommonMixin],
  data() {
    return {
      modalDesc: 'Bạn có chắc xoá bản ghi',
      modalTitle: 'Xoá ',
      apiParams: {
        code: null,
        imeiCode: null,
        customerPhone: null,
        customerName: null,
        startDate: null,
        endDate: null,
        status: ['REJECTED'],
        storeIds: [],
        previousStoreIds: [],
        criteriaIds: [],
        transferStatus: [],
        productName: null,
        primaryTechnicianId: null,
        inspectedByEmployeeId: null,
        warrantyServiceId: null,
        companyId: null,
        creditUnitIds: [],
        paymentCode: null,
      },
      listCompany: [],
      listItem: [],
      numberOfPage: 1,
      totalItems: 0,
      selectedPageSize: null,
      limit: 10,
      optionPageSize: [10, 25, 50, 100],
      fields: [
        {
          key: 'id',
          label: 'ID',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '5%' },
        },
        { key: 'receptionDate', label: 'Ngày TN', sortable: false },
        { key: 'imeiCode', label: 'Sản phẩm', sortable: false, thStyle: { width: '15%' } },
        { key: 'customerPhone', label: 'Khách hàng', sortable: false },
        {
          key: 'statusName',
          label: 'Trạng thái',
          sortable: false,
          tdClass: 'text-center',
          thStyle: { width: '10%' },
        },
        {
          key: 'note',
          label: 'Ghi chú',
          sortable: false,
          thClass: 'note-cell',
          thStyle: { width: '15%' },
        },
        { key: 'storeName', label: 'Tại', sortable: false },
        {
          key: 'paymentStatus',
          label: 'Thanh toán',
          sortable: false,
          tdClass: 'text-center',
          thClass: 'text-center',
          thStyle: { width: '12%' },
        },
        { key: 'actions', label: '', sortable: false, thStyle: { width: '5%' } },
      ],
      statusOptions: WARRANTY_STATUS_OPTIONS.filter((s) => s.id === 'REJECTED'),
      isLoading: false,
      warrantyServiceOptions: [],
      optionStores: [],
      allOptionStores: [],
    }
  },
  components: {
    KTCodePreview,
    Loader,
    WarrantySearchBar,
    WarrantyListTable,
  },
  computed: {
    ...mapGetters(['breadcrumbs', 'pageTitle', 'layoutConfig']),
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'))
    },
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo')
    },
    currentPage() {
      return Number(this.$route.query.page) || 1
    },
    addNewRoute() {
      return '/warranty-rejections/info'
    },
    title() {
      return 'Danh sách Từ chối bảo hành'
    },
    showAddButton() {
      return this.checkPermission('WARRANTY_RECEPTION_INSERT')
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: this.title }])
  },
  async created() {
    try {
      const [{ stores, currentStoreId, currentStoreName, currentStoreShortName }, warrantyServices, companies] =
        await Promise.all([this.fetchStoreByUser(), getWarrantyServices(), this.fetchCompanies()])
      this.warrantyServiceOptions = warrantyServices
      this.listCompany = [{ id: null, name: 'Tất cả doanh nghiệp' }, ...companies]
      this.optionStores = this.mapDataTreeSelect(stores || [], ['id', 'name', 'shortName'])
      if (currentStoreId) {
        this.apiParams.storeIds = [
          {
            id: currentStoreId,
            name: currentStoreName || '',
            shortName: currentStoreShortName || currentStoreName || '',
          },
        ]
      }
      const { data: allStores } = await this.fetchStores()
      this.allOptionStores = this.mapDataTreeSelect(allStores || [], ['id', 'name', 'shortName'])
    } catch (e) {
      this.optionStores = []
      this.allOptionStores = []
    }
    this.fetchData()
  },
  methods: {
    showDeleteAlert,
    onFilter(params) {
      this.apiParams = { ...params }
      this.apiParams.status = ['REJECTED']
      this.$route.query.page = 1
      this.$router.push({
        name: this.$route.name,
      })
      this.fetchData()
    },
    onReset(params) {
      this.apiParams = { ...params }
      this.apiParams.status = ['REJECTED']
      this.selectedPageSize = null
      this.limit = 10
      this.fetchData()
    },
    onPageSizeChange(newSize) {
      this.selectedPageSize = newSize
      this.limit = newSize || 10
      this.fetchData()
    },
    onPageChange(page) {
      this.$router.push({
        name: this.$route.name,
        query: { ...this.$route.query, page },
      })
      this.fetchData()
    },
    editItem(item) {
      this.$router.push({
        name: 'warranty-rejection-info',
        query: { id: item.id },
      })
    },
    viewWarrantyDetail(item) {
      if (!item?.id) return
      this.$router.push({
        name: 'warranty-rejection-info',
        query: { id: item.id, action: 'view' },
      })
    },
    onDeleteItem(item) {
      showDeleteAlert(
        this.modalTitle,
        `${this.modalDesc} ${item.code || item.id}`,
        item,
        this.confirmDeleteItem,
      )
    },
    getParamFilters() {
      return {
        page: this.$route.query.page || 1,
        limit: this.limit,
        id: this.apiParams.id,
        code: this.apiParams.code,
        imeiCode: this.apiParams.imeiCode && this.apiParams.imeiCode.trim(),
        customerPhone: this.apiParams.customerPhone && this.apiParams.customerPhone.trim(),
        customerName: this.apiParams.customerName && this.apiParams.customerName.trim(),
        startDate: formatDBDate(this.apiParams.startDate),
        endDate: formatDBDate(this.apiParams.endDate),
        status: ['REJECTED'],
        storeIds: (this.apiParams.storeIds || []).map((s) => s.id),
        previousStoreIds: (this.apiParams.previousStoreIds || []).map((s) => s.id),
        criteriaIds: (this.apiParams.criteriaIds || []).map((s) => s.id),
        transferStatus: (this.apiParams.transferStatus || []).map((s) => s.id),
        mode: 'reception',
        productName: this.apiParams.productName || null,
        primaryTechnicianId: this.apiParams.primaryTechnicianId || null,
        inspectedByEmployeeId: this.apiParams.inspectedByEmployeeId || null,
        warrantyServiceId: this.apiParams.warrantyServiceId || null,
        companyId: this.apiParams.companyId || null,
        creditUnitIds: (this.apiParams.creditUnitIds || []).map((u) => u.id),
        paymentCode: this.apiParams.paymentCode || null,
      }
    },
    async fetchData() {
      if (this.isLoading) return

      this.isLoading = true
      this.page = this.$route.query.page || 1

      try {
        const params = this.getParamFilters()
        const dataset = await getWarranties(params)

        this.totalItems = dataset.totalRow || 0
        this.numberOfPage = dataset.totalPage || 1
        this.listItem = (dataset.dataset || []).map((item) => mapWarrantyListItem(item))
      } catch (error) {
        console.error('Error fetching data:', error)
        this.totalItems = 0
        this.numberOfPage = 1
        this.listItem = []
      } finally {
        this.isLoading = false
      }
    },
    async deleteItem(id) {
      const success = await deleteWarrantyReception(id)
      if (success) {
        this.fetchData()
      }
    },
    confirmDeleteItem(item) {
      this.deleteItem(item.id)
    },
    mapDataTreeSelect(items = [], keys = ['id', 'name', 'shortName']) {
      if (!items || !items.length) return []
      const [idKey, nameKey, shortNameKey] = keys
      const label = (item) => item[nameKey] || item[shortNameKey] || ''
      const short = (item) => item[shortNameKey] || item[nameKey] || ''
      return items.map((item) => ({
        id: item[idKey],
        name: label(item),
        label: label(item),
        shortName: short(item),
      }))
    },
  },
}
</script>

<style lang="scss" scoped>
.warranty {
  .icon:hover {
    background-color: #90c6fc;
  }

  .bootstrap-datetimepicker-widget.dropdown-menu {
    width: 21em;
  }
}
</style>
